import { defineTerm, getTermRange, defineRange, resolveCycleWindow } from '@magmacomputing/tempo/plugin-api';
import { isNumber } from '@magmacomputing/library';
/** definition of astrological zodiac ranges */
const groups = defineRange([
    // Western (Tropical) - @link https://en.wikipedia.org/wiki/Astrological_sign
    { key: 'Aries', day: 21, month: 3, symbol: 'Ram', trait: 'Daring and energetic, a natural leader', group: 'western' },
    { key: 'Taurus', day: 20, month: 4, symbol: 'Bull', trait: 'Reliable and patient, enjoys luxury', group: 'western' },
    { key: 'Gemini', day: 21, month: 5, symbol: 'Twins', trait: 'Adaptable and intellectual, great communicator', group: 'western' },
    { key: 'Cancer', day: 21, month: 6, symbol: 'Crab', trait: 'Protective and intuitive, deeply emotional', group: 'western' },
    { key: 'Leo', day: 23, month: 7, symbol: 'Lion', trait: 'Confident and ambitious, loves being center stage', group: 'western' },
    { key: 'Virgo', day: 23, month: 8, symbol: 'Virgin', trait: 'Analytical and practical, meticulous and kind', group: 'western' },
    { key: 'Libra', day: 23, month: 9, symbol: 'Scales', trait: 'Diplomatic and fair, values harmony', group: 'western' },
    { key: 'Scorpio', day: 23, month: 10, symbol: 'Scorpion', trait: 'Passionate and resourceful, dynamic and brave', group: 'western' },
    { key: 'Sagittarius', day: 22, month: 11, symbol: 'Archer', trait: 'Optimistic and free-spirited, loves travel', group: 'western' },
    { key: 'Capricorn', day: 22, month: 12, symbol: 'Goat', trait: 'Disciplined and persistent, highly practical', group: 'western' },
    { key: 'Aquarius', day: 20, month: 1, symbol: 'Water Bearer', trait: 'Original and humanitarian, values independence', group: 'western' },
    { key: 'Pisces', day: 19, month: 2, symbol: 'Fish', trait: 'Compassionate and artistic, deeply intuitive', group: 'western' },
    // Chinese (Animal) - @link http://www.creativeartsguild.org/images/uploads/categories/12_Chinese_Zodiac_Signs.pdf
    { key: 'Rat', trait: 'Quick-witted, resourceful', group: 'animal' },
    { key: 'Ox', trait: 'Diligent, dependable', group: 'animal' },
    { key: 'Tiger', trait: 'Brave, confident', group: 'animal' },
    { key: 'Rabbit', trait: 'Quiet, elegant', group: 'animal' },
    { key: 'Dragon', trait: 'Confident, intelligent', group: 'animal' },
    { key: 'Snake', trait: 'Enigmatic, intelligent', group: 'animal' },
    { key: 'Horse', trait: 'Animated, active', group: 'animal' },
    { key: 'Goat', trait: 'Gentle, shy', group: 'animal' },
    { key: 'Monkey', trait: 'Smart, curious', group: 'animal' },
    { key: 'Rooster', trait: 'Observant, hardworking', group: 'animal' },
    { key: 'Dog', trait: 'Loyal, honest', group: 'animal' },
    { key: 'Pig', trait: 'Compassionate, generous', group: 'animal' },
    // Chinese (Element) - @link https://www.timeanddate.com/calendar/aboutelements.html
    { key: 'Wood', group: 'element' },
    { key: 'Fire', group: 'element' },
    { key: 'Earth', group: 'element' },
    { key: 'Metal', group: 'element' },
    { key: 'Water', group: 'element' },
], 'group');
/** resolve the full candidate list for the current context */
function resolve(t, anchor) {
    const list = resolveCycleWindow(t, groups, { anchor, groupBy: ['group'], group: 'western' });
    // calculate the Chinese Zodiac based on the year of the candidate sign
    list.forEach((itm) => {
        const year = itm.year ?? (anchor?.year);
        if (isNumber(year))
            itm['CN'] = getChineseZodiac(year);
    });
    return list;
}
/**
 * ## ZodiacTerm
 * Astrological Zodiac signs
 */
export const ZodiacTerm = defineTerm({
    key: 'zdc',
    scope: 'zodiac',
    description: 'Astrological Zodiac sign',
    groups,
    resolve(anchor) {
        return resolve(this, anchor);
    },
    /** determine where the current Tempo instance fits within the above range */
    define(keyOnly, anchor) {
        return getTermRange(this, resolve(this, anchor), keyOnly, anchor);
    }
});
/** get the chinese zodiac for a given year */
function getChineseZodiac(year) {
    const animals = groups['animal'] ?? [];
    const elements = groups['element'] ?? [];
    if (animals.length === 0 || elements.length === 0)
        return undefined;
    const animalIndex = ((year - 4) % 12 + 12) % 12;
    const elementIndex = Math.floor((((year - 4) % 10) + 10) % 10 / 2);
    const yinYang = year % 2 === 0 ? 'Yang' : 'Yin';
    const animal = animals[animalIndex];
    const element = elements[elementIndex];
    if (!animal || !element)
        return undefined;
    return {
        animal: animal.key,
        trait: animal.trait,
        element: element.key,
        yinYang: yinYang
    };
}
